import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { defineMessages } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import EnumSelect from '@/shared/components/input/EnumSelect';
import DateInput from '@/shared/components/input/DateInput';
import DataCacheService from '@/shared/services/DataCacheService';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import api from '@/shared/api';
import config from '@/config';
import messages from '../invoiceStreamMessages';

const FIELD_SEND_INVOICE_STRATEGY = 1;
const FIELD_START_DATE = 2;
const FIELD_PAYMENT_METHOD = 3;
const FIELD_INVOICE_DOCUMENT_TEMPLATE = 4;
const FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE = 5;
const FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE = 6;
const FIELD_INVOICE_DETAIL_TYPE = 7;
const FIELD_END_DATE = 8;
const FIELD_LEDGER = 9;

const TEMPLATE_TYPE_MAP = {
  [FIELD_INVOICE_DOCUMENT_TEMPLATE]: 0,
  [FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE]: 1,
  [FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE]: 2,
};

const DONT_UPDATE = 'dont-update';
const USE_GENERAL_SETTING = 'use-general-setting';

const localMessages = defineMessages({
  InvoiceStreamStartDateInPastExplanation:
    'If you set this start date in the past, its possible that draft invoices are created instantly based on the schedule.',
  SendInvoiceStrategy: 'Send invoice strategy',
  StartDate: 'Start date',
  EndDate: 'End date',
  PaymentMethod: 'Payment method',
  InvoiceDocumentTemplate: 'Invoice document template',
  InvoiceDetailDocumentTemplate: 'Invoice detail document template',
  CreditNoteDocumentTemplate: 'Credit note document template',
  InvoiceDetailType: 'Invoice detail type',
  UpdateField: 'Update field',
  NoTemplateForLanguage: 'No template found for this language',
  DontUpdate: "Don't update",
  UseRelationSetting: 'Use relation setting',
  FilterOnCompanyRequired: 'Please filter on a single own company first.',
  Ledger: 'Ledger'
});

const isTemplateField = fieldId =>
  fieldId === FIELD_INVOICE_DOCUMENT_TEMPLATE ||
  fieldId === FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE ||
  fieldId === FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE;

export default class UpdateInvoiceStreamFieldModal extends Component {
  state = {
    fields: [],
    selectedField: undefined,
    selectedValue: undefined,
    startDate: moment.utc().startOf('day'),
    endDate: null,
    templateSelections: {},
    allTemplates: [],
    resolvedCompanyId: null,
    loadingTemplates: false,    
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    companyId: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.string),
  };

  static defaultProps = {
    companyId: null,
    languages: [],
  };

  componentDidMount() {
    this.setAvailableFields();
    this.resolveCompanyId();
  }

  resolveCompanyId = () => {
    const { companyId } = this.props;
    if (companyId) {
      this.setState({ resolvedCompanyId: companyId });
      return;
    }
    if (!hasMultipleCompanies()) {
      const defaultCompanyId = DataCacheService.getDefaultCompany()?.id;
      this.setState({ resolvedCompanyId: defaultCompanyId || null });
    }
  };

  setAvailableFields = () => {
    const { intl } = this.props;

    const availableFields = hasAssignments()
      ? [
          {id: FIELD_SEND_INVOICE_STRATEGY, label: intl.formatMessage(localMessages.SendInvoiceStrategy) },
          { id: FIELD_START_DATE, label: intl.formatMessage(localMessages.StartDate) },
          { id: FIELD_END_DATE, label: intl.formatMessage(localMessages.EndDate) },
          { id: FIELD_PAYMENT_METHOD, label: intl.formatMessage(localMessages.PaymentMethod) },
          { id: FIELD_INVOICE_DOCUMENT_TEMPLATE, label: intl.formatMessage(localMessages.InvoiceDocumentTemplate) },
          { id: FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE, label: intl.formatMessage(localMessages.InvoiceDetailDocumentTemplate) },
          { id: FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE, label: intl.formatMessage(localMessages.CreditNoteDocumentTemplate) },
          { id: FIELD_INVOICE_DETAIL_TYPE, label: intl.formatMessage(localMessages.InvoiceDetailType) },
          { id: FIELD_LEDGER, label: intl.formatMessage(localMessages.Ledger) },
        ]
      : [
          { id: FIELD_START_DATE, label: intl.formatMessage(localMessages.StartDate) },
          { id: FIELD_END_DATE, label: intl.formatMessage(localMessages.EndDate) },
          { id: FIELD_LEDGER, label: intl.formatMessage(localMessages.Ledger) },
      ];

    this.setState({ fields: availableFields.sort(dynamicSort('label')) });
  };

  fieldChanged = event => {
    const { fields } = this.state;
    const fieldId = parseInt(event.target.value);
    const field = fields.find(x => x.id === fieldId);

    this.setState(
      {
        selectedField: field,
        selectedValue: undefined,
        startDate: moment.utc().startOf('day'),
        endDate: null,
        templateSelections: {}
      },
      () => {
        if (field && isTemplateField(field.id)) {
          this.loadTemplates();
        }
      },
    );
  };

  loadTemplates = () => {
    const { resolvedCompanyId } = this.state;
    if (!resolvedCompanyId) return;

    this.setState({ loadingTemplates: true });

    api.base
      .get(`${config.appUrls.api}/api/documentTemplate/Templates`, {
        params: { templateTypes: [0, 1, 2], companyId: resolvedCompanyId },
      })
      .then(({ data }) => {
        this.setState({
          allTemplates: data
            .map(t => ({
              id: t.id,
              name: t.name,
              templateType: t.templateType,
              languageCode: t.languageCode,
            }))
            .sort(dynamicSortMultiple('name')),
          loadingTemplates: false,
        });
      });
  };

  templateChanged = (langCode, value) => {
    this.setState(({ templateSelections }) => ({
      templateSelections: { ...templateSelections, [langCode]: value },
    }));
  };

  getTemplatesForLanguage = langCode => {
    const { allTemplates, selectedField } = this.state;
    if (!selectedField || !isTemplateField(selectedField.id)) return [];

    const templateType = TEMPLATE_TYPE_MAP[selectedField.id];
    return allTemplates.filter(t => t.templateType === templateType && t.languageCode === langCode);
  };

  isFormInvalid = () => {
    const {
      selectedField,
      selectedValue,
      startDate,
      endDate,
      templateSelections,
      resolvedCompanyId
    } = this.state;
    if (!selectedField) return true;

    switch (selectedField.id) {
      case FIELD_SEND_INVOICE_STRATEGY:
        return false;
      case FIELD_START_DATE:
        return !startDate || !moment(startDate).isValid();
      case FIELD_END_DATE:
        return false;
      case FIELD_PAYMENT_METHOD:
        return false;
      case FIELD_INVOICE_DOCUMENT_TEMPLATE:
      case FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE:
      case FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE: {
        if (!resolvedCompanyId) return true;
        const hasAnySelection = Object.values(templateSelections).some(v => v !== DONT_UPDATE);
        return !hasAnySelection;
      }
      case FIELD_INVOICE_DETAIL_TYPE:
        return false;
      case FIELD_LEDGER:
        return false;

      default:
        return true;
    }
  };

  onSave = () => {
    const { selectedField, selectedValue, startDate, endDate, templateSelections } = this.state;
    const { onSave } = this.props;

    const result = { field: selectedField.id };

    switch (selectedField.id) {
      case FIELD_SEND_INVOICE_STRATEGY:
        result.sendInvoiceStrategy = selectedValue ?? null;
        break;
      case FIELD_START_DATE:
        result.startDate = startDate.toISOString();
        break;
      case FIELD_END_DATE:
        result.endDate = endDate ? endDate.toISOString() : null;        
        break;
      case FIELD_PAYMENT_METHOD:
        result.paymentMethod = selectedValue ?? null;
        break;
      case FIELD_INVOICE_DOCUMENT_TEMPLATE:
      case FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE:
      case FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE: {
        const documentTemplateIds = {};
        Object.entries(templateSelections).forEach(([langCode, value]) => {
          if (value === DONT_UPDATE) return;
          documentTemplateIds[langCode] = value === USE_GENERAL_SETTING ? null : value;
        });
        result.documentTemplateIds = documentTemplateIds;
        break;
      }
      case FIELD_INVOICE_DETAIL_TYPE:
        result.invoiceDetailType = selectedValue ?? null;
        break;
      case FIELD_LEDGER:
        result.ledger = selectedValue ?? null;
        break;

      default:
        break;
    }

    return onSave(result);
  };

  closeModal = () => {
    const { onClose } = this.props;
    this.setState({
      selectedField: undefined,
      selectedValue: undefined,
      templateSelections: {},
    });
    onClose();
  };

  renderValueInput() {
    const { intl, languages } = this.props;
    const { selectedField, startDate, endDate, templateSelections, loadingTemplates } = this.state;

    if (!selectedField) return null;

    switch (selectedField.id) {
      case FIELD_SEND_INVOICE_STRATEGY:
        return (
          <div className="col-md-6">
            <EnumSelect
              key="sendInvoiceStrategy"
              enumType="sendInvoiceStrategy"
              onChange={value => this.setState({ selectedValue: value })}
              orderByLabel
              allowNull
              placeholder={intl.formatMessage(localMessages.UseRelationSetting)}
            />
          </div>
        );

      case FIELD_START_DATE:
        return (
          <div className="col-md-6">
            <DateInput
              value={startDate}
              onChange={date => this.setState({ startDate: date })}
              isOutsideRange={() => false}
            />
            {startDate && moment(startDate).isBefore(moment().startOf('day')) && (
              <div className="text-warning margin5T">
                {intl.formatMessage(localMessages.InvoiceStreamStartDateInPastExplanation)}
              </div>
            )}
          </div>
        );

      case FIELD_END_DATE:
        return (
          <div className="col-md-6">
            <DateInput
              value={endDate}
              onChange={date => this.setState({ endDate: date })}
              isOutsideRange={() => false}
            />
          </div>
        );

      case FIELD_PAYMENT_METHOD:
        return (
          <div className="col-md-6">
            <EnumSelect
              key="paymentMethod"
              enumType="paymentMethod"
              onChange={value => this.setState({ selectedValue: value })}
              orderByLabel
              allowNull
              placeholder={intl.formatMessage(localMessages.UseRelationSetting)}
            />
          </div>
        );

      case FIELD_INVOICE_DOCUMENT_TEMPLATE:
      case FIELD_INVOICE_DETAIL_DOCUMENT_TEMPLATE:
      case FIELD_CREDIT_NOTE_DOCUMENT_TEMPLATE:
        return this.renderTemplateFields();

      case FIELD_INVOICE_DETAIL_TYPE:
        return (
          <div className="col-md-6">
            <EnumSelect
              key="invoiceDetailType"
              enumType="invoiceDetailType"
              onChange={value => this.setState({ selectedValue: value })}
              allowNull
              placeholder={intl.formatMessage(localMessages.UseRelationSetting)}
            />
          </div>
        );

      case FIELD_LEDGER:
        return (
          <div className="col-md-6">
            <input
              type="text"
              maxLength={10}
              onChange={e => this.setState({ selectedValue: e.target.value })}
              placeholder={intl.formatMessage(localMessages.Ledger)}
            />
          </div>
        );

      default:
        return null;
    }
  }

  renderTemplateFields() {
    const { intl, languages } = this.props;
    const { resolvedCompanyId, templateSelections, loadingTemplates } = this.state;

    if (!resolvedCompanyId) {
      return (
        <div className="col-md-12">
          <div className="alert alert-warning">
            {intl.formatMessage(localMessages.FilterOnCompanyRequired)}
          </div>
        </div>
      );
    }

    return (
      <div className="col-md-12">
        {!loadingTemplates &&
          languages.map(langCode => {
            const templatesForLang = this.getTemplatesForLanguage(langCode);
            const currentValue = templateSelections[langCode] || DONT_UPDATE;
            const langLabel = intl.formatMessage(listItemMsg('language', langCode, langCode));

            return (
              <div key={langCode} className="form-group">
                <label className="control-label col-sm-4">{langLabel}</label>
                <div className="col-sm-8">
                  {templatesForLang.length > 0 ? (
                    <select
                      className="form-control"
                      value={currentValue}
                      onChange={e => this.templateChanged(langCode, e.target.value)}
                    >
                      <option value={DONT_UPDATE}>
                        {intl.formatMessage(localMessages.DontUpdate)}
                      </option>
                      <option value={USE_GENERAL_SETTING}>
                        {intl.formatMessage(localMessages.UseRelationSetting)}
                      </option>
                      {templatesForLang.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="form-control-static text-danger">
                      {intl.formatMessage(localMessages.NoTemplateForLanguage)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  render() {
    const { show, intl } = this.props;
    const { fields, selectedField } = this.state;

    if (!show) return null;

    const isTemplate = selectedField && isTemplateField(selectedField.id);

    return (
      <OkCancelModal
        title={intl.formatMessage(localMessages.UpdateField)}
        show={show}
        onClose={this.closeModal}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <div className={isTemplate ? 'col-md-12' : 'col-md-6'}>
              {fields && (
                <select
                  onChange={this.fieldChanged}
                  className="form-control"
                  value={(selectedField && selectedField.id) || ''}
                >
                  {!selectedField && (
                    <option value="">{intl.formatMessage(messages.SelectAField)}</option>
                  )}
                  {fields.map(field => (
                    <option key={field.id} value={field.id}>
                      {field.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {!isTemplate && this.renderValueInput()}
          </div>
          {isTemplate && <div className="container-fluid">{this.renderValueInput()}</div>}
        </div>
      </OkCancelModal>
    );
  }
}
